import { apiFetch } from '@/lib/api';
import type {
  AnsAppeals,
  AnsCaseDetail,
  AnsCaseList,
  AnsCollection,
  AnsConsistency,
  AnsEntities,
  AnsEntity,
  AnsNip,
  AnsOverview,
  AnsPenalties,
  AnsRegimes,
  AnsSus,
  AnsTimeline,
} from '@/services/types';

/** The fourth agency. Same envelope, same substrate contract, a snapshot corpus. */
export function fetchAnsOverview(): Promise<AnsOverview> {
  return apiFetch<AnsOverview>('/api/v1/ans/overview');
}

export interface AnsCaseQuery {
  query?: string | null;
  track?: string | null;
  object?: string | null;
  /** A board outcome code, `any` (reviewed) or `none` (never reviewed). */
  board_outcome?: string | null;
  trajectory?: string | null;
  year?: number | null;
  credit_status?: string | null;
  /** The operator's full 14-digit CNPJ; anything shorter matches nothing. */
  entity?: string | null;
  case_status?: string | null;
  divida_ativa?: boolean | null;
  limit?: number;
  offset?: number;
}

/**
 * The filter params, without the page bounds, so the CSV export replays the
 * SCREEN's exact query (`limit`/`offset` describe a page, never a result).
 */
export function ansCaseParams(query: AnsCaseQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.track) params.set('track', query.track);
  if (query.object) params.set('object', query.object);
  if (query.board_outcome) params.set('board_outcome', query.board_outcome);
  if (query.trajectory) params.set('trajectory', query.trajectory);
  if (query.year) params.set('year', String(query.year));
  if (query.credit_status) params.set('credit_status', query.credit_status);
  if (query.entity) params.set('entity', query.entity);
  if (query.case_status) params.set('case_status', query.case_status);
  if (query.divida_ativa !== null && query.divida_ativa !== undefined) {
    params.set('divida_ativa', String(query.divida_ativa));
  }
  return params;
}

export function fetchAnsCases(query: AnsCaseQuery): Promise<AnsCaseList> {
  const params = ansCaseParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AnsCaseList>(`/api/v1/ans/cases?${params.toString()}`);
}

/** One penalty. `key` is the NR_DEMANDA digit string, unique across the corpus. */
export function fetchAnsCase(key: string): Promise<AnsCaseDetail> {
  return apiFetch<AnsCaseDetail>(`/api/v1/ans/c/${encodeURIComponent(key)}`);
}

export interface AnsEntityQuery {
  query?: string | null;
  only_penalised?: boolean | null;
  limit?: number;
  offset?: number;
}

export function ansEntityParams(query: AnsEntityQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.only_penalised) params.set('only_penalised', 'true');
  return params;
}

export function fetchAnsEntities(query: AnsEntityQuery): Promise<AnsEntities> {
  const params = ansEntityParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AnsEntities>(`/api/v1/ans/entities?${params.toString()}`);
}

/** One operator, by its full 14-digit CNPJ. */
export function fetchAnsEntity(cnpj: string): Promise<AnsEntity> {
  return apiFetch<AnsEntity>(`/api/v1/ans/e/${encodeURIComponent(cnpj)}`);
}

// --------------------------------------------------------------------------- //
// The ANEEL-shaped screens
// --------------------------------------------------------------------------- //

export function fetchAnsAppeals(): Promise<AnsAppeals> {
  return apiFetch<AnsAppeals>('/api/v1/ans/appeals');
}

export function fetchAnsPenalties(): Promise<AnsPenalties> {
  return apiFetch<AnsPenalties>('/api/v1/ans/penalties');
}

export function fetchAnsCollection(): Promise<AnsCollection> {
  return apiFetch<AnsCollection>('/api/v1/ans/collection');
}

export interface AnsConsistencyQuery {
  object?: string | null;
  track?: string | null;
  limit?: number;
  offset?: number;
}

export function ansConsistencyParams(query: AnsConsistencyQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.object) params.set('object', query.object);
  if (query.track) params.set('track', query.track);
  return params;
}

export function fetchAnsConsistency(query: AnsConsistencyQuery): Promise<AnsConsistency> {
  const params = ansConsistencyParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AnsConsistency>(`/api/v1/ans/consistency?${params.toString()}`);
}

export function fetchAnsTimeline(): Promise<AnsTimeline> {
  return apiFetch<AnsTimeline>('/api/v1/ans/timeline');
}

export function fetchAnsNip(): Promise<AnsNip> {
  return apiFetch<AnsNip>('/api/v1/ans/nip');
}

export function fetchAnsSus(): Promise<AnsSus> {
  return apiFetch<AnsSus>('/api/v1/ans/sus');
}

export function fetchAnsRegimes(): Promise<AnsRegimes> {
  return apiFetch<AnsRegimes>('/api/v1/ans/regimes');
}
